/**
 * The field tickets of a Wireline & Slickline job (fieldiq-api#1345, WL-4). The
 * job's own panels are not edited; these sit beside them, and JobDetailScreen.tsx
 * only gains the two panels.
 *
 *   WlJobTicketsSection  the job's tickets, and "Create ticket" — per job, or
 *                        per day with a date when the job is ticketed per day
 *                        (the job's ticketMode, which the API also decides by).
 *                        A per-day ticket is per UNIT per day: a job running
 *                        more than one unit picks the unit (`unit_id_required`).
 *                        Drafting reads the OFFICE-APPROVED day sheets; a
 *                        refusal (no sheet, not approved, already ticketed, a
 *                        ticket already, the other scope) is a sentence.
 *   WlRentInBillingPanel the billing rate of each rent-in, shown on the job's
 *                        Rent-ins panel: rent-in or other rates only,
 *                        from this job's customer card or the default card
 *                        (`rate_not_rent_in`, `rate_other_customer`). A billable
 *                        rent-in bills on the days it is on rent on a day sheet;
 *                        with no billing rate it drafts onto a ticket unpriced.
 */
import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useCreateWlTicket, useWlRates, useWlRentInBillingRates, useWlTickets, useSetWlRentInBillingRate } from '../../../hooks/useWlTickets';
import { queryLoadFailure } from '../../../lib/load-failure';
import { wlScopeText, wlTicketCoverText, wlTicketRefusal, wlTicketStatusSkin } from '../../../lib/wireline-slickline-tickets';
import type { WlJob, WlJobDetail, WlRentIn } from '../../../types/wireline-slickline-jobs';
import type { WlTicket } from '../../../types/wireline-slickline-tickets';
import { useOpenRecord } from '../../downhole/recordPage';
import { WL_DATE_INPUT, WL_INPUT, WL_PRIMARY, WL_SECONDARY, WlBadge, WlEmpty, WlField, WlFormError, WlPanel, WlRegister, type WlColumn } from '../WlBits';
import { wlTicketTotalText } from './TicketsScreen';
import { wlRateOption, WlUnpricedFlag } from './TicketBits';

/** The kinds a rent-in may bill at (mirrors the API's WL_RENT_IN_RATE_KINDS, lib/wl-rates.ts). */
export const WL_RENT_IN_RATE_KINDS: string[] = ['rent_in', 'other'];

const columns: WlColumn<WlTicket>[] = [
  { key: 'number', header: 'Ticket', render: (t) => <span className="font-mono font-extrabold text-white">{t.ticketNumber || '—'}</span> },
  { key: 'scope', header: 'Covers', render: (t) => wlScopeText(t) },
  { key: 'unit', header: 'Unit', render: (t) => <span className="font-mono">{t.unitNumber || '—'}</span> },
  { key: 'lines', header: 'Lines', align: 'right', render: (t) => String(t.lineCount ?? '—') },
  { key: 'status', header: 'Status', render: (t) => <WlBadge skin={wlTicketStatusSkin(t.status)} value={t.status} /> },
  {
    key: 'total',
    header: 'Subtotal',
    align: 'right',
    render: (t) => (
      <span className="whitespace-nowrap">
        <span className="font-mono">{wlTicketTotalText(t)}</span>
        <WlUnpricedFlag count={t.unpricedCount} />
      </span>
    ),
  },
];

export function WlJobTicketsSection({ job, canWrite, today }: { job: WlJobDetail; canWrite: boolean; today: string | null }) {
  const openRecord = useOpenRecord();
  const query = useWlTickets({ job_id: job.id });
  const create = useCreateWlTicket();
  const perDay = job.ticketMode === 'per_day';
  const [creating, setCreating] = useState(false);
  const [day, setDay] = useState(today ?? '');
  const units = job.units ?? [];
  const [unitId, setUnitId] = useState(units.length === 1 ? units[0].unitId : '');
  const [error, setError] = useState<string | null>(null);
  const tickets = query.data?.data ?? [];

  const draft = () => {
    setError(null);
    if (perDay && !/^\d{4}-\d{2}-\d{2}$/.test(day)) return setError('This job is ticketed per day. Pick the day to ticket.');
    if (perDay && units.length > 1 && !unitId) return setError('This job runs more than one unit, and a per-day ticket is per unit. Pick the unit.');
    create.mutate(
      { jobId: job.id, workDate: perDay ? day : undefined, unitId: perDay && unitId ? unitId : undefined },
      {
        onSuccess: (r) => {
          toast.success(r.data ? `${r.data.ticketNumber} drafted from the day sheets` : 'Ticket drafted');
          setCreating(false);
          if (r.data) openRecord(`/wireline-slickline/tickets/${r.data.id}`);
        },
        onError: (e) => setError(wlTicketRefusal(e, 'The ticket could not be drafted.')),
      },
    );
  };

  return (
    <WlPanel
      eyebrow="Field tickets"
      title={perDay ? 'Ticketed per day' : 'Ticketed per job'}
      testId="wl-job-tickets"
      actions={
        canWrite && !creating ? (
          <button type="button" className={WL_PRIMARY} onClick={() => { setError(null); setCreating(true); }}>
            <Plus className="size-4" aria-hidden="true" />
            Create ticket
          </button>
        ) : undefined
      }
    >
      {creating && (
        <div data-testid="wl-job-ticket-create" className="space-y-3 border-b border-fieldiq-border px-4 py-4">
          <p className="text-sm text-fieldiq-text">
            {perDay
              ? 'Drafts one ticket for the unit and day you pick, from that day’s office-approved sheet. Each line is priced from the rate card where a rate fits; a line with no rate is drafted unpriced.'
              : 'Drafts one ticket covering every office-approved day sheet not already on a ticket. Each line is priced from the rate card where a rate fits; a line with no rate is drafted unpriced.'}
          </p>
          <WlFormError message={error} />
          {perDay && (
            <div className="grid gap-3 sm:grid-cols-[14rem_14rem]">
              <WlField label="Day to ticket *">
                <input className={WL_DATE_INPUT} type="date" aria-label="Day to ticket" value={day} onChange={(e) => setDay(e.target.value)} />
              </WlField>
              {units.length > 1 && (
                <WlField label="Unit *">
                  <select className={WL_INPUT} aria-label="Unit to ticket" value={unitId} onChange={(e) => setUnitId(e.target.value)}>
                    <option value="">Pick one</option>
                    {units.map((u) => (
                      <option key={u.unitId} value={u.unitId}>
                        {u.unitNumber || '—'}
                      </option>
                    ))}
                  </select>
                </WlField>
              )}
            </div>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" className={WL_SECONDARY} onClick={() => setCreating(false)} disabled={create.isPending}>
              Cancel
            </button>
            <button type="button" className={WL_PRIMARY} onClick={draft} disabled={create.isPending}>
              {create.isPending ? 'Drafting…' : 'Draft ticket'}
            </button>
          </div>
        </div>
      )}
      <WlRegister
        query={query}
        rows={tickets}
        columns={columns}
        rail={(t) => wlTicketStatusSkin(t.status).rail}
        onOpen={(t) => openRecord(`/wireline-slickline/tickets/${t.id}`)}
        subject="field tickets"
        testId="wl-job-ticket"
        minWidth="min-w-[720px]"
        empty={<WlEmpty>No tickets for this job yet.</WlEmpty>}
        card={(t) => (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate font-mono text-sm font-extrabold text-white">{t.ticketNumber}</p>
              <p className="mt-0.5 text-xs text-fieldiq-muted">
                {wlTicketCoverText(t)} · Subtotal {wlTicketTotalText(t)}
                <WlUnpricedFlag count={t.unpricedCount} testId="wl-unpriced-flag-card" />
              </p>
            </div>
            <WlBadge skin={wlTicketStatusSkin(t.status)} value={t.status} />
          </div>
        )}
      />
    </WlPanel>
  );
}

function RentInRateRow({
  rentIn,
  current,
  loading,
  failed,
  options,
  canWrite,
}: {
  rentIn: WlRentIn;
  current: { id: string; label: string } | null;
  loading: boolean;
  failed: boolean;
  options: { id: string; label: string }[];
  canWrite: boolean;
}) {
  const set = useSetWlRentInBillingRate();
  const [error, setError] = useState<string | null>(null);
  return (
    <li data-testid="wl-rent-in-billing-row" className="space-y-2 px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-white">{rentIn.description || '—'}</p>
          <p className="text-xs text-fieldiq-muted">
            {rentIn.vendorName || '—'}
            {rentIn.unitNumber ? ` · ${rentIn.unitNumber}` : ''}
          </p>
        </div>
        <div className="w-full sm:w-[28rem]">
          {canWrite ? (
            <select
              className={WL_INPUT}
              aria-label={`Billing rate for ${rentIn.description}`}
              value={current?.id ?? ''}
              disabled={loading || failed || set.isPending}
              onChange={(e) => {
                setError(null);
                set.mutate(
                  { rentInId: rentIn.id, rateId: e.target.value || null },
                  {
                    onSuccess: () => toast.success(e.target.value ? 'Billing rate set' : 'Billing rate cleared'),
                    onError: (err) => setError(wlTicketRefusal(err, 'The billing rate could not be set.')),
                  },
                );
              }}
            >
              <option value="">{loading ? 'Loading…' : 'Not billed from the rate card'}</option>
              {current && !options.some((o) => o.id === current.id) && <option value={current.id}>{current.label}</option>}
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-sm text-fieldiq-text">{loading ? '…' : current?.label ?? '—'}</p>
          )}
        </div>
      </div>
      {failed && <WlFormError message="This rent-in's billing rate could not be read." />}
      <WlFormError message={error} />
    </li>
  );
}

export function WlRentInBillingPanel({ job, rentIns, canWrite }: { job: WlJob; rentIns: WlRentIn[]; canWrite: boolean }) {
  const ratesQuery = useWlRates(undefined, canWrite);
  const billing = useWlRentInBillingRates(rentIns.map((r) => r.id));
  const options = (ratesQuery.data?.data ?? [])
    .filter((r) => WL_RENT_IN_RATE_KINDS.includes(r.kind) && (r.customerId === null || r.customerId === job.customerId))
    .map((r) => ({ id: r.id, label: wlRateOption(r) }));
  return (
    <WlPanel eyebrow="Billing" title="Rent-in billing rates" testId="wl-rent-in-billing">
      <p className="border-b border-fieldiq-border px-4 py-3 text-sm text-fieldiq-muted">
        What the customer is billed for each rent-in, from the rate card — a rent-in or other rate on this customer’s card or the default card. A
        billable rent-in bills on each day sheet it is on rent for, and one with no billing rate drafts onto a ticket unpriced.
      </p>
      {canWrite && queryLoadFailure(ratesQuery) && <div className="px-4 pt-3"><WlFormError message="The rate card could not be read, so a billing rate cannot be picked right now." /></div>}
      {!rentIns.length ? (
        <WlEmpty>No rent-ins on this job.</WlEmpty>
      ) : (
        <ul className="divide-y divide-fieldiq-border">
          {rentIns.map((ri, i) => {
            const q = billing[i];
            const rate = q?.data?.data?.rate ?? null;
            return (
              <RentInRateRow
                key={ri.id}
                rentIn={ri}
                current={rate ? { id: rate.id, label: wlRateOption(rate) } : null}
                loading={!!q?.isLoading}
                failed={!!q && !!queryLoadFailure(q)}
                options={options}
                canWrite={canWrite}
              />
            );
          })}
        </ul>
      )}
    </WlPanel>
  );
}

